import type { SignatureBlock } from '@blogforge/shared';
import type { DragPath } from '../../types';
import { useEditorStore } from '../../stores/editorStore';

interface SignatureInspectorProps {
  block: SignatureBlock;
  path: DragPath;
}

export function SignatureInspector({ block, path }: SignatureInspectorProps) {
  const updateBlock = useEditorStore((s) => s.updateBlock);
  const attrs = block.attrs;

  const set = (patch: Partial<SignatureBlock['attrs']>) => {
    updateBlock(block.id, { attrs: { ...attrs, ...patch } }, path);
  };

  const setSocial = (index: number, patch: Partial<{ platform: string; url: string }>) => {
    const socials = (attrs.socials || []).map((s, i) => (i === index ? { ...s, ...patch } : s));
    set({ socials });
  };

  const removeSocial = (index: number) => {
    set({ socials: (attrs.socials || []).filter((_, i) => i !== index) });
  };

  const addSocial = () => {
    set({ socials: [...(attrs.socials || []), { platform: 'Website', url: 'https://' }] });
  };

  return (
    <div className="space-y-4">
      <div className="field-label font-medium">Signature</div>
      <div className="space-y-3">
        <div>
          <label className="field-label">Name</label>
          <input
            className="field-input"
            value={attrs.name}
            placeholder="Jane Doe"
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>
        <div>
          <label className="field-label">Title</label>
          <input
            className="field-input"
            value={attrs.title || ''}
            placeholder="Product Designer"
            onChange={(e) => set({ title: e.target.value })}
          />
        </div>
        <div>
          <label className="field-label">Company</label>
          <input
            className="field-input"
            value={attrs.company || ''}
            placeholder="Acme Inc."
            onChange={(e) => set({ company: e.target.value })}
          />
        </div>
        <div>
          <label className="field-label">Avatar URL</label>
          <input
            className="field-input"
            value={attrs.avatarSrc || ''}
            placeholder="https://…/avatar.png"
            onChange={(e) => set({ avatarSrc: e.target.value })}
          />
        </div>
        <div>
          <label className="field-label">Avatar alt text</label>
          <input
            className="field-input"
            value={attrs.avatarAlt || ''}
            placeholder="Jane Doe"
            onChange={(e) => set({ avatarAlt: e.target.value })}
          />
        </div>
        <div>
          <label className="field-label">Logo / signature image URL (GIF ok)</label>
          <input
            className="field-input"
            value={attrs.logoSrc || ''}
            placeholder="https://…/signature.gif"
            onChange={(e) => set({ logoSrc: e.target.value })}
          />
          <p className="mt-1 text-xs text-muted">
            Keep it small (&lt;200 KB). Renders as a static first frame in Outlook; the name above
            stays real text, so it's spam-safe.
          </p>
        </div>
        <div>
          <label className="field-label">Logo alt text</label>
          <input
            className="field-input"
            value={attrs.logoAlt || ''}
            placeholder="Acme Inc. logo"
            onChange={(e) => set({ logoAlt: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="field-label font-medium">Social links</div>
        {attrs.socials?.map((social, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              className="field-input w-1/3"
              value={social.platform}
              placeholder="X"
              onChange={(e) => setSocial(i, { platform: e.target.value })}
            />
            <input
              className="field-input flex-1"
              value={social.url}
              placeholder="https://x.com/…"
              onChange={(e) => setSocial(i, { url: e.target.value })}
            />
            <button
              type="button"
              className="text-sm text-danger hover:text-danger"
              onClick={() => removeSocial(i)}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className="text-xs font-semibold text-accent hover:text-ink"
          onClick={addSocial}
        >
          + Add social link
        </button>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <label className="field-label mb-0">Variant</label>
          <select
            className="field-input"
            value={attrs.variant}
            onChange={(e) => set({ variant: e.target.value as 'compact' | 'card' })}
          >
            <option value="compact">Compact</option>
            <option value="card">Card</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <label className="field-label mb-0">Layout</label>
          <select
            className="field-input"
            value={attrs.layout}
            onChange={(e) => set({ layout: e.target.value as 'left' | 'center' })}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
          </select>
        </div>
      </div>
    </div>
  );
}
